import {
  collection,
  doc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { COLLECTIONS } from "@/integrations/firebase/collections";
import { db } from "@/integrations/firebase/firestore";

/** The admin's ACTIVE impersonation request, or null when there is none.
 *  The stopping caller may not be the one that started (the banner mounts
 *  its own hook instance, and a sign-out has none), so it is looked up. */
export async function findActiveRequestId(
  adminUid: string,
): Promise<string | null> {
  const snapshot = await getDocs(
    query(
      collection(db, COLLECTIONS.IMPERSONATION_REQUESTS),
      where("adminId", "==", adminUid),
      where("status", "==", "ACTIVE"),
      limit(1),
    ),
  );
  return snapshot.docs[0]?.id ?? null;
}

/**
 * Asks for this admin's impersonation to end, without waiting for it to.
 *
 * For the two callers that must not block on the trigger: signing out, and a
 * sign-in finding a claim left from an earlier one (Jira NAV-130). Returns
 * whether a request was filed; having nothing to stop is not a failure.
 * `useImpersonation().stop` is the path that waits and refreshes the token.
 */
export async function requestImpersonationStop(
  adminUid: string,
): Promise<boolean> {
  const requestId = await findActiveRequestId(adminUid);
  if (!requestId) return false;
  await updateDoc(doc(db, COLLECTIONS.IMPERSONATION_REQUESTS, requestId), {
    status: "STOP_REQUESTED",
    updatedAt: serverTimestamp(),
  });
  return true;
}
